import type { Currency } from '@ps/web-core-enums';
import { Size } from '@ps/web-core-enums';
import { useScreenSize } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { CasinoGameList, CasinoSliderButtons, useWidgetAnchor } from '@swf/casino';
import type { SliderHandles, State as SliderState } from '@swf/components/Slider/Slider';
import useCurrencyFormatter from '@swf/core/hooks/useCurrencyFormatter';
import type { ShowField } from '@swf/core/types/CasinoGameList';
import type { WidgetFC } from '@swf/core/types/Widget';
import { getStyle } from '@swf/core/utils/casino';

import {
  TXTCLI_win_up_to,
  TXTWCL_Paid_today,
  TXTWCL_Won_today
} from './CasinoJackpotWidget.messages.json';
import styles from './CasinoJackpotWidget.scss';
import jackpotBadge from './assets/jackpot-badge.png';
import JackpotInfoBlock from './components/JackpotInfoBlock';
import type { CasinoJackpotWidgetState } from './model';
import { jackpotWidgetModel, useJackpotWidgetModel } from './model';
import { adjustFontSizeToFit, getMaxFontSize, plainText } from './utils';

const CasinoJackpotWidget: WidgetFC = () => {
  const { widgetSelect, widgetConfig } = useJackpotWidgetModel();
  const widgetState: CasinoJackpotWidgetState = useSelector(widgetSelect.get);
  const sliderRef = React.createRef<SliderHandles>();
  const containerRef = React.createRef<HTMLDivElement>();
  const [sliderState, setSliderState] = useState<SliderState>({ left: false, right: false });
  const { greaterThan } = useScreenSize();
  const isDesktop = greaterThan(Size.Large);
  const {
    layout,
    render_info: renderInfo,
    featured_tab: featuredTab,
    params,
    description
  } = widgetConfig;
  const { winUpTo, wonToday, paidToday, currency, gameConfigs: games } = widgetState;

  const widgetTargetId = useWidgetAnchor();
  const casinoGameListProps = {
    params,
    games,
    renderInfo: renderInfo as ShowField[],
    loading: false
  };

  const amountWinUpTo = useCurrencyFormatter()({
    value: winUpTo,
    currency: currency as Currency
  });

  const amountPaidToday = useCurrencyFormatter()({
    value: paidToday,
    currency: currency as Currency
  });

  const amountWonToday = plainText(wonToday);

  // Refs for each JackpotInfoBlock
  const winUpToRef = useRef<HTMLParagraphElement>(null);
  const paidTodayRef = useRef<HTMLParagraphElement>(null);
  const wonTodayRef = useRef<HTMLParagraphElement>(null);
  const maxFontSize = getMaxFontSize();
  const [fontSize, setFontSize] = useState<number>(maxFontSize);

  const adjustFontSize = useCallback(() => {
    const elements = [winUpToRef.current, paidTodayRef.current, wonTodayRef.current].filter(
      (el) => el !== null
    ) as HTMLElement[];
    const maxFontSize = getMaxFontSize();

    if (elements.length) {
      const calculatedFontSize = adjustFontSizeToFit(elements, maxFontSize);
      setFontSize(calculatedFontSize);
    }
  }, [winUpToRef, paidTodayRef, wonTodayRef]);

  useEffect(() => {
    adjustFontSize();
  }, [amountWinUpTo, amountPaidToday, amountWonToday, adjustFontSize]);

  useEffect(() => {
    window.addEventListener('resize', adjustFontSize);
    return () => {
      window.removeEventListener('resize', adjustFontSize);
    };
  }, [adjustFontSize]);

  if (!games.length) {
    return null;
  }
  return (
    <section
      className={styles.container}
      style={getStyle(layout)}
      ref={containerRef}
      id={widgetTargetId}
    >
      <div className={styles.wrapper}>
        <div className={styles.tabsWrap}>
          <div
            className={classNames(styles.firstBlockContainer, {
              [styles.active]: featuredTab === 'win'
            })}
          >
            <JackpotInfoBlock
              ref={winUpToRef}
              title={TXTCLI_win_up_to}
              amount={amountWinUpTo}
              fontSize={fontSize}
            />
          </div>
          <img src={jackpotBadge} alt="Jackpot Badge" />
          <div className={classNames(styles.infoBlockContainer)}>
            <JackpotInfoBlock title={TXTCLI_win_up_to} amount={amountWinUpTo} fontSize={fontSize} />
            <JackpotInfoBlock
              className={classNames({
                [styles.withBorder]: featuredTab === 'paid'
              })}
              ref={paidTodayRef}
              title={TXTWCL_Paid_today}
              amount={amountPaidToday}
              fontSize={fontSize}
            />
            <JackpotInfoBlock
              className={classNames({
                [styles.withBorder]: featuredTab === 'won'
              })}
              ref={wonTodayRef}
              title={TXTWCL_Won_today}
              amount={amountWonToday}
              fontSize={fontSize}
            />
          </div>
        </div>
        <div className={styles.headerButtons}>
          {isDesktop && (
            <div className={styles.sliderButtons}>
              <CasinoSliderButtons sliderRef={sliderRef} sliderState={sliderState} />
            </div>
          )}
        </div>
        <div className={styles.sliderContainer}>
          <CasinoGameList
            jackpotWidget
            rows={1}
            icon="icnSquare"
            onScroll={setSliderState}
            sliderRef={sliderRef}
            overlayHierarchy="jackpot_exclusive"
            {...casinoGameListProps}
          />
        </div>
        <div className={classNames(styles.description, styles.descriptionMobile)}>
          <span>{description}</span>
        </div>
      </div>
      <div className={classNames(styles.description, styles.descriptionDesktop)}>
        <span>{description}</span>
      </div>
    </section>
  );
};
CasinoJackpotWidget.model = jackpotWidgetModel;
export default CasinoJackpotWidget;
